import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getInvite, getFamily, joinFamily, joinFamilyAsHouseholdMember, joinFamilyAsMergeTarget } from '@coparent/shared/src/services/familyService';
import { HiUserGroup, HiUsers, HiHome, HiSwitchHorizontal } from 'react-icons/hi';
import toast from 'react-hot-toast';

const InviteHandler = () => {
  const { code } = useParams();
  const { currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvite = async () => {
      if (!currentUser) return;
      try {
        const inviteData = await getInvite(code);
        if (!inviteData) {
          setError('This invite link is invalid.');
          setLoading(false);
          return;
        }
        if (inviteData.status === 'accepted') {
          setError('This invite link has already been used.');
          setLoading(false);
          return;
        }
        if (inviteData.expiresAt && new Date(inviteData.expiresAt.seconds ? inviteData.expiresAt.seconds * 1000 : inviteData.expiresAt) < new Date()) {
          setError('This invite link has expired.');
          setLoading(false);
          return;
        }
        setInvite(inviteData);

        const familyData = await getFamily(inviteData.familyId);
        if (!familyData) {
          setError('The family associated with this invite no longer exists.');
          setLoading(false);
          return;
        }
        setFamily(familyData);
      } catch (err) {
        console.error('InviteHandler fetchInvite error:', err);
        if (err.code === 'permission-denied') {
          setError('You do not have permission to view this invite.');
        } else {
          setError('Something went wrong loading this invite.');
        }
      }
      setLoading(false);
    };
    fetchInvite();
  }, [code, currentUser]);

  // Not authenticated — redirect to register with invite code
  if (!currentUser) {
    return <Navigate to={`/register?code=${code}`} />;
  }

  const handleJoin = async () => {
    setJoining(true);
    try {
      const type = invite.type || 'coparent';

      if (type === 'coparent') {
        const result = await joinFamily(currentUser.uid, currentUser.displayName, code);
        if (result.joinedAs === 'household_member') {
          toast.success('The co-parent role is already filled \u2014 you\'ve been added as a household member.');
        } else {
          toast.success('Joined as co-parent!');
        }
      } else if (type === 'household') {
        await joinFamilyAsHouseholdMember(currentUser.uid, currentUser.displayName, code, invite.householdOf);
        toast.success('Joined as a household member!');
      } else if (type === 'merge') {
        await joinFamilyAsMergeTarget(currentUser.uid, currentUser.displayName, code);
        toast.success('Account linked successfully!');
      }

      await refreshUserData();
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Failed to join family');
    }
    setJoining(false);
  };

  const getInviteInfo = () => {
    const type = invite?.type || 'coparent';
    if (type === 'coparent') {
      return {
        icon: <HiUsers className="text-2xl text-parent1-600" />,
        iconBg: 'bg-parent1-100',
        title: 'Co-Parent Invite',
        description: `You've been invited to join the ${family?.name} family as a co-parent.`
      };
    }
    if (type === 'household') {
      const parentName = family?.parentProfiles?.[invite.householdOf]?.displayName;
      return {
        icon: <HiHome className="text-2xl text-purple-600" />,
        iconBg: 'bg-purple-100',
        title: 'Household Invite',
        description: `You've been invited to join ${parentName ? `${parentName}'s` : 'a'} household in the ${family?.name} family.`
      };
    }
    if (type === 'merge') {
      const targetName = invite.mergeTargetType === 'parent'
        ? family?.parentProfiles?.[invite.mergeTargetId]?.displayName
        : family?.householdMembers?.[invite.mergeTargetId]?.displayName;
      return {
        icon: <HiSwitchHorizontal className="text-2xl text-accent-600" />,
        iconBg: 'bg-accent-100',
        title: 'Account Link',
        description: `You've been invited to take over ${targetName ? `${targetName}'s` : 'a'} spot in the ${family?.name} family.`
      };
    }
    return { icon: <HiUserGroup className="text-2xl text-ink-500" />, iconBg: 'bg-surface-100', title: 'Invite', description: '' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <div className="text-ink-400 text-sm font-medium">Loading invite...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 px-4">
        <div className="max-w-sm w-full animate-slide-up">
          <div className="card p-7 text-center">
            <div className="mx-auto w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-5">
              <HiUserGroup className="text-2xl text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-ink-900 mb-2">Invite Error</h2>
            <p className="text-sm text-ink-400 mb-6">{error}</p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary w-full">
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const info = getInviteInfo();

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 px-4">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-shared-100/40 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-parent1-100/40 blur-3xl" />
      </div>
      <div className="max-w-sm w-full relative animate-slide-up">
        <div className="text-center mb-8">
          <div className={`mx-auto w-14 h-14 ${info.iconBg} rounded-2xl flex items-center justify-center mb-5 shadow-warm`}>
            {info.icon}
          </div>
          <h2 className="text-2xl font-bold text-ink-900 tracking-tight">{info.title}</h2>
        </div>

        <div className="card p-7">
          <div className="text-center mb-6">
            <p className="text-sm text-ink-500 leading-relaxed">{info.description}</p>
            {family?.name && (
              <div className="mt-4 p-3 bg-surface-50 rounded-xl border border-surface-200">
                <p className="text-xs text-ink-400 font-semibold uppercase tracking-wide">Family</p>
                <p className="font-bold text-ink-900 mt-0.5">{family.name}</p>
              </div>
            )}
          </div>

          <button
            onClick={handleJoin}
            disabled={joining}
            className="btn-primary w-full"
          >
            {joining ? 'Joining...' : 'Join Family'}
          </button>

          <button
            onClick={() => navigate('/dashboard')}
            className="w-full mt-3 text-sm text-ink-400 hover:text-ink-600 font-medium transition-colors py-2"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteHandler;
